/**
 * EditNameDialog — single-field edit modal for the user's display name.
 *
 * Plain `useState` + the `Input` primitive (no RHF) — the form has one
 * field, RHF setup would be more boilerplate than logic. The submit
 * runs the wire schema through Zod for the same validation the API
 * applies, so the error message is consistent between the two layers
 * (no "Name is required" here vs "name is required" from the server).
 *
 * On success the dialog closes and `identity.me` is invalidated by the
 * caller (lifted up so all the post-mutation cleanup lives in one place
 * on the page).
 */
import * as React from "react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Alert,
} from "@flint/ui";
import { identity as contract } from "@flint/api-contract";
import { trpc } from "../../lib/trpc.ts";

export interface EditNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName: string;
  /** Called after the mutation succeeds — caller invalidates `identity.me`. */
  onUpdated: () => void;
}

export function EditNameDialog({
  open,
  onOpenChange,
  initialName,
  onUpdated,
}: EditNameDialogProps) {
  const [name, setName] = React.useState(initialName);
  const [clientError, setClientError] = React.useState<string | null>(null);

  // Reset the input when the dialog re-opens so a previous abandoned edit
  // doesn't pre-fill stale text.
  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setClientError(null);
    }
  }, [open, initialName]);

  const update = trpc.identity.updateProfile.useMutation({
    onSuccess: () => {
      onUpdated();
      onOpenChange(false);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    // Server-shape validation upfront so the user sees field-level
    // errors without a roundtrip. The contract's `.refine` for "at least
    // one field" passes here because `name` is set.
    const parsed = contract.updateProfileInput.safeParse({ name });
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message ?? "Invalid name.");
      return;
    }
    if (parsed.data.name === initialName) {
      // No-change shortcut: skip the round-trip; the server would reject
      // with UserNoChangesError → 400, which would feel like a bug for
      // the user who just clicked Save without editing.
      onOpenChange(false);
      return;
    }
    update.mutate({ name: parsed.data.name });
  };

  // The wire error is mapped to .data.cause.reason in our reraise() chain.
  const wireError = update.error?.message ?? null;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Edit name</DialogTitle>
            <DialogDescription>
              This appears on invoices, audit logs, and inside the workspace.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 flex flex-col gap-2">
            <Label htmlFor="profile-name">Display name</Label>
            <Input
              id="profile-name"
              autoFocus
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={update.isPending}
            />
            {clientError ? <p className="text-xs text-bad">{clientError}</p> : null}
            {wireError && !clientError ? (
              <Alert intent="bad" title="Couldn't save" description={wireError} />
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
